import React from 'react'
import { DimensionValue, Platform, useWindowDimensions, View, ViewStyle } from 'react-native'

type BreakPoints<T> = {
  default: T extends unknown ? T : number | string
  xs: T extends unknown ? T : number | string
  sm: T extends unknown ? T : number | string
  md: T extends unknown ? T : number | string
  lg: T extends unknown ? T : number | string
  xl: T extends unknown ? T : number | string
  '2xl': T extends unknown ? T : number | string
}

export const defaultBreakPoints = {
  default: 0, // 100%
  xs: 480,
  sm: 640,
  md: 768,
  lg: 992, // 1024
  xl: 1280,
  '2xl': 1536 // 1536
}

type UseBreakPointValueConfig = {
  breakPoints: typeof defaultBreakPoints
}
type UseBreakPointValueProps<T> = Partial<BreakPoints<T>>

export const useBreakPointValue = <T extends unknown | undefined>(
  breakpointValues: UseBreakPointValueProps<T>,
  config: UseBreakPointValueConfig = {
    breakPoints: defaultBreakPoints
  }
) => {
  const dimensions = useWindowDimensions()
  if (!config?.breakPoints) {
    throw 'useBreakPointValue: config.breakpoints not defined'
  }

  const matchBreakpoint = Object.entries(config.breakPoints).findLast(([key, breakpoint]) => {
    if (typeof breakpoint === 'number' && Object.keys(breakpointValues).includes(key)) {
      return dimensions.width >= breakpoint
    }
    return false
  })

  if (matchBreakpoint) {
    const [matchedKey] = matchBreakpoint
    return breakpointValues[matchedKey as keyof BreakPoints<T>]
  }
  return breakpointValues?.default
}

export const isWeb = Platform.OS === 'web'

export function useMaxWidth() {
  const breakpointValue = useBreakPointValue(
    {
      default: isWeb ? 'inherit' : '100%',
      sm: defaultBreakPoints.sm.toString()
      // md: defaultBreakPoints.md.toString(),
      // lg: defaultBreakPoints.lg.toString(),
      // xl: defaultBreakPoints.xl.toString()
      // '2xl': defaultBreakPoints['2xl'].toString()
    },
    {
      breakPoints: defaultBreakPoints
    }
  )

  if (!Number.isNaN(Number(breakpointValue))) {
    return Number(breakpointValue)
  }

  return (breakpointValue as DimensionValue) ?? 'auto'
}

type WebWrapperProps = React.PropsWithChildren & {
  backgroundColor?: ViewStyle['backgroundColor']
  style?: React.ComponentProps<typeof View>['style']
}

export const WebWrapper = React.forwardRef<View, WebWrapperProps>((props, ref) => {
  const { children, backgroundColor = 'transparent', style, ...rest } = props

  const maxWidth = useMaxWidth()

  if (!isWeb) {
    return children
  }

  return (
    <View
      id="web-wrapper"
      ref={ref}
      {...rest}
      style={[
        {
          maxWidth: maxWidth ? parseInt(maxWidth.toString()) : '100%',
          width: '100%',
          marginHorizontal: 'auto',
          backgroundColor,
          // minHeight: 'auto',
          flex: 1
        },
        style
      ]}
    >
      {children}
    </View>
  )
})
